import React, { MouseEventHandler, useState } from "react";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import { ConfiguratorModes } from "utils/enums";
import { Instant } from "@js-joda/core";

import useDashboardStore from "store/dashboard-store";
import { Button } from "primereact/button";
import { I, T } from "services/lang/LanguageService";
import { PageSize } from "api/model/dashboard/DashboardReportOptions";
import DashboardComponentConfiguratorNew, {
  defaultComponent,
} from "../group-component/GroupComponentConfiguratorNew";
import GroupConfigurator from "./GroupConfigurator";

import VerticalLayout from "../layouts/vertical/VerticalLayout";
import HorizontalFlexLayout from "../layouts/horizontal-flex/HorizontalFlexLayout";
import "../../dashboard-styles/DashboardTabGroup.scss";
import { ReorderType } from "./reorder/DraggableItem";
import Reorder, { ComponentReorderConfig } from "./reorder/Reorder";
import MoveGroupOrComponent from "../group-component/MoveGroupOrComponent";
import { GridLayoutComponent } from "../layouts/grid/GridLayout";
import { ContextMenuItem } from "../layouts/interfaces";
import GroupHeaderComponentConfigurator from "../group-component/GroupHeaderComponentConfigurator";
import DashboardComponent from "../group-component/GroupComponent";
import "./DashboardTabGroup.scss";

interface DashboardTabGroupProps {
  group: DashboardComponentGroup;
  reportPageSize?: PageSize;
  tabId: number;
  isEditMode: boolean;
  onContextMenu: MouseEventHandler<HTMLDivElement>;
  setCurrentContextMenuItems: (currentContextMenuItems: ContextMenuItem[]) => void;
  refTime?: Instant;
}

const DashboardTabGroup = ({
  group,
  tabId,
  reportPageSize,
  isEditMode,
  onContextMenu,
  setCurrentContextMenuItems,
  refTime,
}: DashboardTabGroupProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const tabContextConfig = useDashboardStore(state => state.getTabContextConfig(tabId));
  const [isCollapsed, setIsCollapsed] = useState<boolean>((group.isCollapsed && group.collapsable) ?? false);

  const [groupConfiguratorVisibility, setGroupConfiguratorVisibility] = useState<boolean>(false);
  const [moveTabGroupDialogVisibility, setMoveTabGroupDialogVisibility] = useState<boolean>(false);
  const [dashboardComponentCreationDialogVisibility, setDashboardComponentCreationDialogVisibility] =
    useState<boolean>(false);
  const [groupHeaderComponentCreationDialogVisibility, setGroupHeaderComponentCreationDialogVisibility] =
    useState<boolean>(false);
  const [isComponentsReorderEnabled, setIsComponentsReorderEnabled] = useState<ComponentReorderConfig>({
    isVisible: false,
  });

  const getLayoutComponent = () => {
    switch (group.layout.type) {
      case DashboardLayoutType.VERTICAL:
        return VerticalLayout;
      case DashboardLayoutType.GRID:
        return GridLayoutComponent;
      case DashboardLayoutType.HORIZONTAL_FLEX:
      default:
        return HorizontalFlexLayout;
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  const LayoutComponent = getLayoutComponent();
  return (
    <div
      className={`position-relative border-round mt-2 ${
        isEditMode ? "border-500 border-2" : `${tabContextConfig?.showBorders ? "group-container" : ""}`
      } dashboard-tab-group-container`}
      style={{ position: "relative", minHeight: "10rem" }}
    >
      {((group.title && group.title !== "") ||
        isEditMode ||
        (group.headerConfig?.components && group.headerConfig.components.length > 0)) && (
        <div className="group-header">
          <div className="flex flex-row align-items-center gap-2">
            {group.collapsable && (
              <i
                className={`pi ${isCollapsed ? "pi-plus" : "pi-minus"}`}
                onClick={toggleCollapse}
                aria-label={isCollapsed ? T("action.expand") : T("action.collapse")}
                style={{ fontSize: "1.5rem" }}
              />
            )}
            <div className="group-title">{I(group.title || "")}</div>
          </div>
          <div className="flex flex-row flex-wrap gap-2 align-items-center justify-content-end">
            {group.headerConfig?.components.map(component => (
              <div key={component.id} onContextMenu={onContextMenu}>
                <DashboardComponent
                  groupId={group.id}
                  component={component}
                  layoutType={group.layout.type}
                  setCurrentContextMenuItems={setCurrentContextMenuItems}
                  isGroupHeader
                  tabId={tabId}
                  isReports={!!reportPageSize}
                  refTime={refTime}
                />
              </div>
            ))}
            {isEditMode && dashboard && (
              <Button
                icon="pi pi-cog"
                tooltip={T("action.configure-group")}
                label="Configure"
                className={`${reportPageSize ? "component" : "dashboard"}-editor-button icon-orange`}
                onClick={() => setGroupConfiguratorVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />
            )}
          </div>
        </div>
      )}
      {!isCollapsed && (
        <LayoutComponent
          components={group.components}
          layoutType={group.layout.type}
          groupId={group.id}
          align={group.layout.align}
          justify={group.layout.justify}
          onContextMenu={onContextMenu}
          setCurrentContextMenuItems={setCurrentContextMenuItems}
          tabId={tabId}
          reportPageSize={reportPageSize}
          isEditMode={isEditMode}
          refTime={refTime}
        />
      )}
      {isComponentsReorderEnabled && (
        <Reorder
          setReorderDialogVisibility={setIsComponentsReorderEnabled}
          reorderDialogVisibility={isComponentsReorderEnabled.isVisible}
          reorderType={isComponentsReorderEnabled.type || ReorderType.COMPONENTS}
          group={group}
          tabId={tabId}
        />
      )}
      {dashboardComponentCreationDialogVisibility && isEditMode && (
        <DashboardComponentConfiguratorNew
          isDialogVisible={dashboardComponentCreationDialogVisibility}
          mode={ConfiguratorModes.ADD}
          setIsDialogVisible={setDashboardComponentCreationDialogVisibility}
          component={defaultComponent(group.layout.type)}
          layoutType={group.layout.type}
          groupId={group.id}
          tabId={tabId}
          isReports={!!reportPageSize}
        />
      )}
      {groupConfiguratorVisibility && isEditMode && (
        <GroupConfigurator
          tabId={tabId}
          group={group}
          isDialogVisible={groupConfiguratorVisibility}
          setIsDialogVisible={setGroupConfiguratorVisibility}
          setIsComponentsReorderEnabled={setIsComponentsReorderEnabled}
          setMoveTabGroupDialogVisibility={setMoveTabGroupDialogVisibility}
          setDashboardComponentCreationDialogVisibility={setDashboardComponentCreationDialogVisibility}
          setGroupHeaderComponentCreationDialogVisibility={setGroupHeaderComponentCreationDialogVisibility}
        />
      )}
      {groupHeaderComponentCreationDialogVisibility && isEditMode && (
        <GroupHeaderComponentConfigurator
          isVisible={groupHeaderComponentCreationDialogVisibility}
          setIsVisible={setGroupHeaderComponentCreationDialogVisibility}
          mode={ConfiguratorModes.ADD}
          groupId={group.id}
          tabId={tabId}
        />
      )}
      {moveTabGroupDialogVisibility && dashboard && (
        <MoveGroupOrComponent
          dashboardId={dashboard?.id}
          isVisible={moveTabGroupDialogVisibility}
          setIsVisible={setMoveTabGroupDialogVisibility}
          groupId={group.id}
          group={group}
          tabId={tabId}
          isComponent={false}
        />
      )}
    </div>
  );
};

export default DashboardTabGroup;
